import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Eye, MessageSquare, Calendar, DollarSign, Home, Target, ArrowUp, ArrowDown, Briefcase, Trophy, Users, Activity, CheckCircle, Clock } from 'lucide-react';

const COLORS = ['#1a365d', '#3182ce', '#63b3ed', '#90cdf4', '#bee3f8'];
const STATUS_COLORS = {
  active: '#38a169',
  sold: '#e53e3e',
  rented: '#3182ce',
  pending: '#d69e2e',
  unknown: '#a0aec0'
};
const DEAL_STATUS_COLORS = {
  pending: '#d69e2e',
  in_progress: '#3182ce',
  closed_won: '#38a169',
  closed_lost: '#e53e3e'
};
const DEAL_STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In Progress',
  closed_won: 'Won',
  closed_lost: 'Lost'
};

export default function PropertyAnalytics({ API_URL, viewAll = false }) {
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [statusBreakdown, setStatusBreakdown] = useState(null);
  const [listingBreakdown, setListingBreakdown] = useState(null);
  const [dealsAnalytics, setDealsAnalytics] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendDays, setTrendDays] = useState(30);
  const [activeTab, setActiveTab] = useState('properties');

  useEffect(() => {
    fetchAllAnalytics();
  }, [trendDays, viewAll]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    const scope = viewAll ? '?scope=all' : '?scope=auto';
    try {
      const [analyticsRes, trendsRes, statusRes, listingRes, dealsRes, perfRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/properties${scope}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/trends${scope}&days=${trendDays}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/status-breakdown${scope}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/listing-type-breakdown${scope}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/deals${scope}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/performance${scope}`, { credentials: 'include' })
      ]);

      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (trendsRes.ok) setTrends(await trendsRes.json());
      if (statusRes.ok) setStatusBreakdown(await statusRes.json());
      if (listingRes.ok) setListingBreakdown(await listingRes.json());
      if (dealsRes.ok) setDealsAnalytics(await dealsRes.json());
      if (perfRes.ok) setPerformanceData(await perfRes.json());
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const properties = analytics?.properties || [];
  const dailyData = trends?.daily_data || [];
  const statusData = statusBreakdown?.status_breakdown || [];
  const listingData = listingBreakdown?.listing_breakdown || [];
  const dealsSummary = dealsAnalytics?.summary || {};
  const dealStatusBreakdown = dealsAnalytics?.status_breakdown || [];
  const monthlyTrend = dealsAnalytics?.monthly_trend || [];
  const topAgents = dealsAnalytics?.top_agents || [];
  const recentClosed = dealsAnalytics?.recent_closed || [];

  // Calculate week-over-week change for trends
  const recentWeek = dailyData.slice(-7);
  const previousWeek = dailyData.slice(-14, -7);
  const recentInquiries = recentWeek.reduce((sum, d) => sum + d.inquiries, 0);
  const previousInquiries = previousWeek.reduce((sum, d) => sum + d.inquiries, 0);
  const inquiryChange = previousInquiries > 0 
    ? ((recentInquiries - previousInquiries) / previousInquiries * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-6" data-testid="property-analytics">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-2xl font-serif">{viewAll ? "Business Analytics" : "My Analytics"}</h2>
        <p className="text-muted-foreground text-sm">
          {viewAll ? "Performance overview of all properties across the business" : "Performance overview of your properties"}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit" data-testid="analytics-tabs">
        <button
          onClick={() => setActiveTab('properties')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'properties'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="tab-properties"
        >
          <Home className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Properties
        </button>
        <button
          onClick={() => setActiveTab('deals')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'deals'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="tab-deals"
        >
          <Briefcase className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Deals & Sales
        </button>
        <button
          onClick={() => setActiveTab('performance')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'performance'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="tab-performance"
        >
          <Activity className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Performance
        </button>
      </div>

      {activeTab === 'properties' && (
        <PropertiesTab
          summary={summary}
          dailyData={dailyData}
          statusData={statusData}
          listingData={listingData}
          properties={properties}
          trendDays={trendDays}
          setTrendDays={setTrendDays}
          inquiryChange={inquiryChange}
        />
      )}

      {activeTab === 'deals' && (
        <DealsTab
          dealsSummary={dealsSummary}
          dealStatusBreakdown={dealStatusBreakdown}
          monthlyTrend={monthlyTrend}
          topAgents={topAgents}
          recentClosed={recentClosed}
          viewAll={viewAll}
        />
      )}

      {activeTab === 'performance' && (
        <PerformanceTab data={performanceData} viewAll={viewAll} />
      )}
    </div>
  );
}

/* =================== PROPERTIES TAB =================== */
function PropertiesTab({ summary, dailyData, statusData, listingData, properties, trendDays, setTrendDays, inquiryChange }) {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          icon={<Home className="w-5 h-5" />}
          label="Total Properties"
          value={summary.total_properties || 0}
          color="bg-blue-500"
        />
        <SummaryCard
          icon={<Eye className="w-5 h-5" />}
          label="Total Views"
          value={summary.total_views?.toLocaleString() || 0}
          subtext={`${summary.avg_views || 0} avg/property`}
          color="bg-green-500"
        />
        <SummaryCard
          icon={<MessageSquare className="w-5 h-5" />}
          label="Total Inquiries"
          value={summary.total_inquiries || 0}
          color="bg-purple-500"
        />
        <SummaryCard
          icon={<Target className="w-5 h-5" />}
          label="Conversion Rate"
          value={`${summary.conversion_rate || 0}%`}
          subtext="inquiries to deals"
          color="bg-orange-500"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inquiry & Tour Trends */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Activity Trends
            </h3>
            <select
              value={trendDays}
              onChange={(e) => setTrendDays(Number(e.target.value))}
              className="text-sm border rounded px-2 py-1"
              data-testid="trend-period-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
            </select>
          </div>
          
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="display_date" 
                  tick={{ fontSize: 11 }}
                  interval={Math.floor(dailyData.length / 6)}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="inquiries" stroke="#1a365d" strokeWidth={2} dot={false} name="Inquiries" />
                <Line type="monotone" dataKey="tours" stroke="#38a169" strokeWidth={2} dot={false} name="Tour Bookings" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">No trend data available</div>
          )}
          
          {inquiryChange !== 0 && (
            <div className="mt-2 flex items-center gap-1 text-sm">
              {inquiryChange > 0 ? <ArrowUp className="w-4 h-4 text-green-500" /> : <ArrowDown className="w-4 h-4 text-red-500" />}
              <span className={inquiryChange > 0 ? 'text-green-600' : 'text-red-600'}>
                {Math.abs(inquiryChange)}% inquiries vs last week
              </span>
            </div>
          )}
        </div>

        {/* Property Status Breakdown */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Home className="w-5 h-5 text-blue-600" />
            Property Status
          </h3>
          
          {statusData.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="count" nameKey="status" label={({ count }) => `${count}`}>
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {statusData.map((item, index) => (
                  <div key={item.status} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[item.status] || COLORS[index] }} />
                      <span className="text-sm capitalize">{item.status}</span>
                    </div>
                    <span className="font-medium">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">No status data available</div>
          )}
        </div>
      </div>

      {/* Listing Type Breakdown */}
      {listingData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            Listing Type Breakdown
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {listingData.map((item) => (
              <div key={item.type} className="bg-gray-50 rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium capitalize text-lg">{item.type === 'sale' ? 'For Sale' : 'For Rent'}</span>
                  <span className="text-2xl font-bold text-blue-600">{item.count}</span>
                </div>
                <div className="text-sm text-gray-600">
                  Total Value: <span className="font-medium">K {item.total_value?.toLocaleString() || 0}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Properties Table */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Property Performance
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="property-performance-table">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 font-medium">Property</th>
                <th className="text-center p-3 font-medium">Status</th>
                <th className="text-center p-3 font-medium">Views</th>
                <th className="text-center p-3 font-medium">Inquiries</th>
                <th className="text-center p-3 font-medium">Tours</th>
                <th className="text-center p-3 font-medium">Deals</th>
                <th className="text-center p-3 font-medium">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {properties.slice(0, 10).map((prop) => (
                <tr key={prop.property_id} className="border-b hover:bg-gray-50">
                  <td className="p-3">
                    <div className="font-medium text-gray-800 truncate max-w-[200px]">{prop.title}</div>
                    <div className="text-xs text-gray-500">{prop.ref_number}</div>
                  </td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                      prop.status === 'active' ? 'bg-green-100 text-green-700' :
                      prop.status === 'sold' ? 'bg-red-100 text-red-700' :
                      prop.status === 'rented' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{prop.status}</span>
                  </td>
                  <td className="p-3 text-center font-medium">{prop.views}</td>
                  <td className="p-3 text-center">{prop.inquiries}</td>
                  <td className="p-3 text-center">{prop.tours}</td>
                  <td className="p-3 text-center">{prop.deals}</td>
                  <td className="p-3 text-center">
                    <span className={`font-medium ${prop.conversion_rate > 20 ? 'text-green-600' : prop.conversion_rate > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
                      {prop.conversion_rate}%
                    </span>
                  </td>
                </tr>
              ))}
              {properties.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-gray-500">No properties found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {properties.length > 10 && (
          <div className="mt-3 text-center text-sm text-gray-500">Showing top 10 of {properties.length} properties</div>
        )}
      </div>
    </div>
  );
}

/* =================== DEALS TAB =================== */
function DealsTab({ dealsSummary, dealStatusBreakdown, monthlyTrend, topAgents, recentClosed, viewAll }) {
  return (
    <div className="space-y-6" data-testid="deals-analytics">
      {/* Deals Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          icon={<Briefcase className="w-5 h-5" />}
          label="Total Deals"
          value={dealsSummary.total_deals || 0}
          subtext={`Pipeline: K ${(dealsSummary.total_pipeline_value || 0).toLocaleString()}`}
          color="bg-blue-500"
        />
        <SummaryCard
          icon={<DollarSign className="w-5 h-5" />}
          label="Total Revenue"
          value={`K ${(dealsSummary.total_revenue || 0).toLocaleString()}`}
          subtext={`${dealsSummary.total_closed || 0} closed deals`}
          color="bg-emerald-500"
        />
        <SummaryCard
          icon={<Target className="w-5 h-5" />}
          label="Avg Deal Value"
          value={`K ${(dealsSummary.avg_deal_value || 0).toLocaleString()}`}
          color="bg-indigo-500"
        />
        <SummaryCard
          icon={<Trophy className="w-5 h-5" />}
          label="Win Rate"
          value={`${dealsSummary.win_rate || 0}%`}
          subtext="closed won / total"
          color="bg-amber-500"
        />
      </div>

      {/* Deal Pipeline & Revenue Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deal Status Pipeline */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-600" />
            Deal Pipeline
          </h3>
          {dealStatusBreakdown.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={dealStatusBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="status"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(s) => DEAL_STATUS_LABELS[s] || s}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value, name) => [name === 'value' ? `K ${value.toLocaleString()}` : value, name === 'value' ? 'Value' : 'Count']}
                    labelFormatter={(s) => DEAL_STATUS_LABELS[s] || s}
                  />
                  <Bar dataKey="count" name="Deals" radius={[4, 4, 0, 0]}>
                    {dealStatusBreakdown.map((entry, i) => (
                      <Cell key={i} fill={DEAL_STATUS_COLORS[entry.status] || COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {dealStatusBreakdown.map((item) => (
                  <div key={item.status} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: DEAL_STATUS_COLORS[item.status] || '#a0aec0' }} />
                    <span className="text-gray-600">{DEAL_STATUS_LABELS[item.status] || item.status}:</span>
                    <span className="font-medium">{item.count}</span>
                    <span className="text-gray-400">(K {item.value.toLocaleString()})</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">No deals data available</div>
          )}
        </div>

        {/* Monthly Revenue Trend */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Monthly Revenue
          </h3>
          {monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="display" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `K ${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value, name) => [name === 'revenue' ? `K ${value.toLocaleString()}` : value, name === 'revenue' ? 'Revenue' : 'Deals']} />
                <Legend />
                <Bar dataKey="revenue" name="Revenue" fill="#38a169" radius={[4, 4, 0, 0]} />
                <Bar dataKey="deals" name="Deals" fill="#3182ce" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">No revenue data yet</div>
          )}
        </div>
      </div>

      {/* Top Agents (business view only) */}
      {viewAll && topAgents.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Top Performing Agents
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="top-agents-table">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium">#</th>
                  <th className="text-left p-3 font-medium">Agent</th>
                  <th className="text-center p-3 font-medium">Deals Won</th>
                  <th className="text-right p-3 font-medium">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {topAgents.map((agent, i) => (
                  <tr key={agent.agent_id} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium text-gray-500">{i + 1}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <img
                          src={agent.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name)}&size=32`}
                          alt={agent.name}
                          className="w-7 h-7 rounded-full"
                        />
                        <span className="font-medium">{agent.name}</span>
                      </div>
                    </td>
                    <td className="p-3 text-center font-medium">{agent.deals}</td>
                    <td className="p-3 text-right font-semibold text-emerald-600">K {agent.revenue.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Closed Deals */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-600" />
          Recent Closed Deals
        </h3>
        {recentClosed.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-deals-table">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium">Deal</th>
                  {viewAll && <th className="text-left p-3 font-medium">Agent</th>}
                  <th className="text-right p-3 font-medium">Value</th>
                  <th className="text-right p-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentClosed.map((deal) => (
                  <tr key={deal.deal_id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div className="font-medium text-gray-800">{deal.title}</div>
                      {deal.notes && <div className="text-xs text-gray-500 truncate max-w-[200px]">{deal.notes}</div>}
                    </td>
                    {viewAll && <td className="p-3 text-gray-600">{deal.agent_name || '-'}</td>}
                    <td className="p-3 text-right font-semibold text-emerald-600">K {(deal.value || 0).toLocaleString()}</td>
                    <td className="p-3 text-right text-gray-500 text-xs">
                      {deal.created_at ? new Date(deal.created_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">
            <Briefcase className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p>No closed deals yet</p>
          </div>
        )}
      </div>
    </div>
  );
}
/* =================== PERFORMANCE TAB =================== */
function PerformanceTab({ data, viewAll }) {
  if (!data) {
    return <div className="py-8 text-center text-gray-500">No performance data available</div>;
  }

  const { lead_funnel = [], inquiry_metrics = {}, tour_metrics = {}, tour_status_data = [], monthly_activity = [] } = data;

  const FUNNEL_COLORS = {
    new: '#3182ce', contacted: '#63b3ed', qualified: '#d69e2e',
    proposal: '#ed8936', negotiation: '#9f7aea', won: '#38a169', lost: '#e53e3e'
  };

  return (
    <div className="space-y-6" data-testid="performance-analytics">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          icon={<Users className="w-5 h-5" />}
          label="Total Leads"
          value={lead_funnel.reduce((sum, s) => sum + s.count, 0)}
          subtext={`${lead_funnel.find(s => s.stage === 'new')?.count || 0} new`}
          color="bg-blue-500"
        />
        <SummaryCard
          icon={<MessageSquare className="w-5 h-5" />}
          label="Inquiries"
          value={inquiry_metrics.total || 0}
          subtext={`${inquiry_metrics.reply_rate || 0}% reply rate`}
          color="bg-purple-500"
        />
        <SummaryCard
          icon={<Calendar className="w-5 h-5" />}
          label="Tours"
          value={tour_metrics.total || 0}
          subtext={`${tour_metrics.completion_rate || 0}% completed`}
          color="bg-teal-500"
        />
        <SummaryCard
          icon={<CheckCircle className="w-5 h-5" />}
          label="Reply Rate"
          value={`${inquiry_metrics.reply_rate || 0}%`}
          subtext={`${inquiry_metrics.pending || 0} pending`}
          color="bg-amber-500"
        />
      </div>

      {/* Lead Funnel + Tour Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Funnel */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Lead Funnel
          </h3>
          {lead_funnel.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={lead_funnel} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="stage" tick={{ fontSize: 11, textTransform: 'capitalize' }} width={80} />
                <Tooltip />
                <Bar dataKey="count" name="Leads" radius={[0, 4, 4, 0]}>
                  {lead_funnel.map((entry, i) => (
                    <Cell key={i} fill={FUNNEL_COLORS[entry.stage] || COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">No leads data</div>
          )}
        </div>

        {/* Tour Status */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-teal-600" />
            Tour Status Breakdown
          </h3>
          {tour_status_data.some(t => t.count > 0) ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie data={tour_status_data.filter(t => t.count > 0)} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="count" nameKey="status" label={({ count }) => `${count}`}>
                    {tour_status_data.filter(t => t.count > 0).map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-3">
                {tour_status_data.map((item) => (
                  <div key={item.status} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm">{item.status}</span>
                    </div>
                    <span className="font-medium">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">No tour data</div>
          )}
        </div>
      </div>

      {/* Monthly Activity */}
      {monthly_activity.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            Monthly Activity (Last 6 Months)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthly_activity}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="display" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="listings" name="New Listings" fill="#1a365d" radius={[4, 4, 0, 0]} />
              <Bar dataKey="inquiries" name="Inquiries" fill="#3182ce" radius={[4, 4, 0, 0]} />
              <Bar dataKey="tours" name="Tours" fill="#38a169" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Inquiry & Tour Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            Inquiry Stats
          </h3>
          <div className="space-y-3">
            <StatRow label="Total Inquiries" value={inquiry_metrics.total || 0} />
            <StatRow label="Replied" value={inquiry_metrics.replied || 0} color="text-green-600" />
            <StatRow label="Pending" value={inquiry_metrics.pending || 0} color="text-amber-600" />
            <StatRow label="Last 30 Days" value={inquiry_metrics.recent_30d || 0} color="text-blue-600" />
            <div className="pt-2 border-t">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Reply Rate</span>
                <span className="font-bold text-lg">{inquiry_metrics.reply_rate || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(inquiry_metrics.reply_rate || 0, 100)}%` }} />
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-teal-600" />
            Tour Stats
          </h3>
          <div className="space-y-3">
            <StatRow label="Total Tours" value={tour_metrics.total || 0} />
            <StatRow label="Completed" value={tour_metrics.completed || 0} color="text-green-600" />
            <StatRow label="Confirmed" value={tour_metrics.confirmed || 0} color="text-blue-600" />
            <StatRow label="Cancelled" value={tour_metrics.cancelled || 0} color="text-red-600" />
            <div className="pt-2 border-t">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Completion Rate</span>
                <span className="font-bold text-lg">{tour_metrics.completion_rate || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                <div className="bg-teal-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(tour_metrics.completion_rate || 0, 100)}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatRow({ label, value, color = "text-gray-800" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`font-semibold ${color}`}>{value}</span>
    </div>
  );
}


function SummaryCard({ icon, label, value, subtext, color }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border p-4" data-testid={`summary-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-3">
        <div className={`${color} text-white p-2 rounded-lg`}>
          {icon}
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-800">{value}</div>
          <div className="text-sm text-gray-500">{label}</div>
          {subtext && <div className="text-xs text-gray-400">{subtext}</div>}
        </div>
      </div>
    </div>
  );
}
